import React, { useState, useEffect } from 'react';
import Modal from './Modal';
import { DataSource } from '../services/dataSourceService';

interface DataSourceSelectionModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (selectedDataSourceIds: string[]) => void;
  initialSelection: string[];
  databaseSources: DataSource[];
  fileSources: DataSource[];
  loading: boolean;
}

const DataSourceSelectionModal: React.FC<DataSourceSelectionModalProps> = ({
  isOpen,
  onClose,
  onSave,
  initialSelection,
  databaseSources,
  fileSources,
  loading
}) => {
  const [selectedDataSources, setSelectedDataSources] = useState<string[]>(initialSelection);
  const [activeTab, setActiveTab] = useState<'databases' | 'files'>('databases');

  // Reset selected data sources when initial selection changes
  useEffect(() => {
    setSelectedDataSources(initialSelection);
  }, [initialSelection]);

  // Also reset when modal opens
  useEffect(() => {
    if (isOpen) {
      setSelectedDataSources(initialSelection);
    }
  }, [isOpen, initialSelection]);

  const handleCheckboxChange = (dataSourceId: string) => {
    setSelectedDataSources(prev => {
      if (prev.includes(dataSourceId)) {
        return prev.filter(id => id !== dataSourceId);
      } else {
        return [...prev, dataSourceId];
      }
    });
  };

  const handleSave = () => {
    onSave(selectedDataSources);
  };

  const handleSelectAll = (type: 'databases' | 'files') => {
    const sources = type === 'databases' ? databaseSources : fileSources;
    const sourceIds = sources.map(source => source.id);
    
    setSelectedDataSources(prev => {
      // Add all source IDs that aren't already selected
      const newIds = sourceIds.filter(id => !prev.includes(id));
      return [...prev, ...newIds];
    });
  };

  const handleDeselectAll = (type: 'databases' | 'files') => {
    const sources = type === 'databases' ? databaseSources : fileSources;
    const sourceIds = sources.map(source => source.id);
    
    setSelectedDataSources(prev => {
      // Remove all source IDs of this type
      return prev.filter(id => !sourceIds.includes(id));
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Select Data Sources">
      <div className="data-source-selection-modal">
        <style>
          {`
            .data-source-selection-modal {
              display: flex;
              flex-direction: column;
              height: 100%;
            }
            
            .data-source-tabs {
              display: flex;
              margin-bottom: 20px;
              border-bottom: 1px solid var(--border);
            }
            
            .data-source-tab {
              padding: 10px 20px;
              cursor: pointer;
              border-bottom: 2px solid transparent;
              transition: all 0.3s ease;
              font-weight: 500;
              color: var(--text-secondary);
            }
            
            .data-source-tab.active {
              color: var(--primary);
              border-bottom: 2px solid var(--primary);
            }
            
            .data-source-tab:hover:not(.active) {
              color: var(--text-primary);
              background-color: var(--bg-tertiary);
            }
            
            .data-source-list {
              flex: 1;
              overflow-y: auto;
              max-height: 400px;
              margin-bottom: 20px;
            }
            
            .data-source-item {
              display: flex;
              align-items: center;
              padding: 12px 15px;
              border: 1px solid var(--border);
              border-radius: 8px;
              margin-bottom: 10px;
              background-color: var(--bg-tertiary);
              transition: all 0.2s ease;
            }
            
            .data-source-item:hover {
              background-color: var(--hover-bg);
              border-color: var(--primary);
            }
            
            .data-source-checkbox {
              margin-right: 15px;
              width: 18px;
              height: 18px;
              cursor: pointer;
            }
            
            .data-source-info {
              flex: 1;
            }
            
            .data-source-name {
              font-weight: 600;
              color: var(--text-primary);
              margin-bottom: 4px;
            }
            
            .data-source-details {
              font-size: 0.85rem;
              color: var(--text-secondary);
              display: flex;
              gap: 15px;
            }
            
            .data-source-actions {
              display: flex;
              gap: 10px;
              margin-bottom: 10px;
            }
            
            .select-all-btn, .deselect-all-btn {
              padding: 8px 12px;
              border-radius: 6px;
              font-size: 0.9rem;
              cursor: pointer;
              transition: all 0.2s ease;
              border: 1px solid var(--border);
            }
            
            .select-all-btn {
              background-color: var(--primary);
              color: white;
              border-color: var(--primary);
            }
            
            .select-all-btn:hover {
              background-color: var(--primary-dark);
            }
            
            .deselect-all-btn {
              background-color: var(--bg-tertiary);
              color: var(--text-primary);
            }
            
            .deselect-all-btn:hover {
              background-color: var(--hover-bg);
            }
            
            .modal-actions {
              display: flex;
              justify-content: flex-end;
              gap: 12px;
              padding-top: 20px;
              border-top: 1px solid var(--border);
              margin-top: auto;
            }
            
            .btn {
              padding: 12px 20px;
              border-radius: 8px;
              border: none;
              cursor: pointer;
              font-weight: 500;
              font-size: 14px;
              transition: all 0.3s ease;
              min-width: 100px;
              text-align: center;
            }
            
            .btn-primary {
              background: linear-gradient(135deg, var(--primary) 0%, var(--secondary) 100%);
              color: white;
              box-shadow: 0 4px 15px rgba(59, 130, 246, 0.3);
            }
            
            .btn-primary:hover {
              transform: translateY(-2px);
              box-shadow: 0 6px 20px rgba(59, 130, 246, 0.4);
            }
            
            .btn-secondary {
              background-color: var(--bg-tertiary);
              color: var(--text-primary);
              border: 1px solid var(--border);
            }
            
            .btn-secondary:hover {
              background-color: var(--hover-bg);
              border-color: var(--primary);
              transform: translateY(-1px);
            }
            
            .loading {
              text-align: center;
              padding: 20px;
              color: var(--text-secondary);
            }
            
            .empty-state {
              text-align: center;
              padding: 40px 20px;
              color: var(--text-secondary);
            }
            
            .empty-state p {
              margin: 0;
            }
          `}
        </style>
        
        <div className="data-source-tabs">
          <div 
            className={`data-source-tab ${activeTab === 'databases' ? 'active' : ''}`}
            onClick={() => setActiveTab('databases')}
          >
            Databases ({databaseSources.length})
          </div>
          <div 
            className={`data-source-tab ${activeTab === 'files' ? 'active' : ''}`}
            onClick={() => setActiveTab('files')}
          >
            Files ({fileSources.length})
          </div>
        </div>
        
        {loading ? (
          <div className="loading">Loading data sources...</div>
        ) : (
          <>
            {activeTab === 'databases' && (
              <div className="data-source-list">
                {databaseSources.length === 0 ? (
                  <div className="empty-state">
                    <p>No database sources available.</p>
                  </div>
                ) : (
                  <>
                    <div className="data-source-actions">
                      <button 
                        className="select-all-btn" 
                        onClick={() => handleSelectAll('databases')}
                      >
                        Select All
                      </button>
                      <button 
                        className="deselect-all-btn" 
                        onClick={() => handleDeselectAll('databases')}
                      >
                        Deselect All
                      </button>
                    </div>
                    {databaseSources.map(source => (
                      <div key={source.id} className="data-source-item">
                        <input
                          type="checkbox"
                          className="data-source-checkbox"
                          checked={selectedDataSources.includes(source.id)}
                          onChange={() => handleCheckboxChange(source.id)}
                        />
                        <div className="data-source-info">
                          <div className="data-source-name">{source.name}</div>
                          <div className="data-source-details">
                            <span>{source.type}</span>
                            {source.created_at && (
                              <span>Created: {new Date(source.created_at).toLocaleDateString()}</span>
                            )}
                            {source.status && <span>Status: {source.status}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
            
            {activeTab === 'files' && (
              <div className="data-source-list">
                {fileSources.length === 0 ? (
                  <div className="empty-state">
                    <p>No file sources available.</p>
                  </div>
                ) : (
                  <>
                    <div className="data-source-actions">
                      <button 
                        className="select-all-btn" 
                        onClick={() => handleSelectAll('files')}
                      >
                        Select All
                      </button>
                      <button 
                        className="deselect-all-btn" 
                        onClick={() => handleDeselectAll('files')}
                      >
                        Deselect All
                      </button>
                    </div>
                    {fileSources.map(source => (
                      <div key={source.id} className="data-source-item">
                        <input
                          type="checkbox"
                          className="data-source-checkbox"
                          checked={selectedDataSources.includes(source.id)}
                          onChange={() => handleCheckboxChange(source.id)}
                        />
                        <div className="data-source-info">
                          <div className="data-source-name">{source.name}</div>
                          <div className="data-source-details">
                            <span>{(source.file_type || '').toUpperCase()}</span>
                            {source.file_size && <span>Size: {source.file_size} bytes</span>}
                            {source.created_at && (
                              <span>Created: {new Date(source.created_at).toLocaleDateString()}</span>
                            )}
                            {source.status && <span>Status: {source.status}</span>}
                          </div>
                        </div>
                      </div>
                    ))}
                  </>
                )}
              </div>
            )}
          </>
        )}
        
        <div className="modal-actions">
          <button className="btn btn-secondary" onClick={onClose} disabled={loading}>
            Cancel
          </button>
          <button className="btn btn-primary" onClick={handleSave} disabled={loading}>
            Save Changes
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default DataSourceSelectionModal;